<script setup>
import { computed, ref } from 'vue';
import { formatNumber, seriesMax } from './metricsHelpers';

const props = defineProps({
  dates: { type: Array, required: true },
  values: { type: Array, required: true },
  label: { type: String, required: true },
  format: { type: Function, default: formatNumber },
  formatDate: { type: Function, default: date => date },
});

const W = 640;
const H = 180;
const PAD = { top: 12, right: 12, bottom: 24, left: 48 };
const hovered = ref(null);

const max = computed(() => seriesMax(props.values));
const x = index => {
  const span = W - PAD.left - PAD.right;
  return props.dates.length <= 1
    ? PAD.left + span / 2
    : PAD.left + (index * span) / (props.dates.length - 1);
};
const y = value =>
  PAD.top +
  (H - PAD.top - PAD.bottom) * (1 - (max.value ? value / max.value : 0));

// Dia sem dado quebra a linha: nunca desenhamos um zero que não existiu.
const segments = computed(() => {
  const result = [];
  let current = [];
  props.values.forEach((value, index) => {
    if (typeof value === 'number') current.push([x(index), y(value)]);
    else if (current.length) {
      result.push(current);
      current = [];
    }
  });
  if (current.length) result.push(current);
  return result;
});
const linePath = seg =>
  seg.map(([px, py], i) => `${i ? 'L' : 'M'}${px},${py}`).join('');
const areaPath = seg =>
  `${linePath(seg)}L${seg[seg.length - 1][0]},${H - PAD.bottom}L${seg[0][0]},${H - PAD.bottom}Z`;
const ticks = computed(() => (max.value ? [0, max.value / 2, max.value] : [0]));
const xLabels = computed(() => {
  const n = props.dates.length;
  if (!n) return [];
  return [...new Set([0, Math.floor((n - 1) / 2), n - 1])].map(i => ({
    i,
    text: props.formatDate(props.dates[i]),
  }));
});

const select = index => {
  hovered.value = index;
};
const onMove = event => {
  const n = props.dates.length;
  if (!n) return;
  const rect = event.currentTarget.getBoundingClientRect();
  const svgX = ((event.clientX - rect.left) / rect.width) * W;
  const step = n > 1 ? (W - PAD.left - PAD.right) / (n - 1) : 1;
  select(Math.min(n - 1, Math.max(0, Math.round((svgX - PAD.left) / step))));
};

// Sem mouse, a seta parte do último dia (o mais recente) e anda a partir dali.
const moveSelection = delta => {
  const n = props.dates.length;
  if (!n) return;
  const base = hovered.value === null ? n - 1 : hovered.value;
  select(Math.min(n - 1, Math.max(0, base + delta)));
};
const onKeydown = event => {
  if (event.key === 'ArrowRight') {
    event.preventDefault();
    moveSelection(1);
  } else if (event.key === 'ArrowLeft') {
    event.preventDefault();
    moveSelection(-1);
  } else if (event.key === 'Escape') {
    hovered.value = null;
  }
};
defineExpose({ select });
</script>

<template>
  <figure class="relative">
    <svg
      :viewBox="`0 0 ${W} ${H}`"
      tabindex="0"
      class="h-auto w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
      role="img"
      :aria-label="label"
      @mousemove="onMove"
      @mouseleave="hovered = null"
      @keydown="onKeydown"
      @blur="hovered = null"
    >
      <g v-for="tick in ticks" :key="tick">
        <line
          :x1="PAD.left"
          :x2="W - PAD.right"
          :y1="y(tick)"
          :y2="y(tick)"
          class="stroke-n-slate-4"
          stroke-width="1"
        />
        <text
          :x="PAD.left - 8"
          :y="y(tick) + 4"
          text-anchor="end"
          class="fill-n-slate-10 text-[10px] tabular-nums"
        >
          {{ format(Math.round(tick)) }}
        </text>
      </g>
      <path
        v-for="(seg, i) in segments"
        :key="`area-${i}`"
        :d="areaPath(seg)"
        class="fill-n-blue-9/10"
      />
      <path
        v-for="(seg, i) in segments"
        :key="`line-${i}`"
        data-test="line"
        :d="linePath(seg)"
        class="stroke-n-blue-9"
        fill="none"
        stroke-width="2"
        stroke-linejoin="round"
        stroke-linecap="round"
      />
      <text
        v-for="item in xLabels"
        :key="item.i"
        :x="x(item.i)"
        :y="H - 6"
        text-anchor="middle"
        class="fill-n-slate-10 text-[10px]"
      >
        {{ item.text }}
      </text>
      <g v-if="hovered !== null">
        <line
          :x1="x(hovered)"
          :x2="x(hovered)"
          :y1="PAD.top"
          :y2="H - PAD.bottom"
          class="stroke-n-slate-8"
          stroke-width="1"
        />
        <circle
          v-if="typeof values[hovered] === 'number'"
          :cx="x(hovered)"
          :cy="y(values[hovered])"
          r="4"
          class="fill-n-blue-9 stroke-n-solid-1"
          stroke-width="2"
        />
      </g>
    </svg>
    <figcaption
      v-if="hovered !== null"
      data-test="tooltip"
      aria-live="polite"
      class="pointer-events-none absolute right-2 top-0 rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1 text-xs shadow-sm"
    >
      <span class="text-n-slate-11">{{ formatDate(dates[hovered]) }}</span>
      <span class="ml-2 font-semibold tabular-nums text-n-slate-12">{{
        format(values[hovered])
      }}</span>
    </figcaption>
  </figure>
</template>
